import { useState, useMemo, useEffect } from 'react';
import ReviewForm from '../../../components/ReviewForm/ReviewForm';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../context/AuthContext';

import PageHeader from '../../../components/common/ui/PageHeader';
import FullCustomCardWithHeader from '../../../components/common/ui/FullCustomCardWithHeader';
import BookingReshedule from '../../../components/common/ui/BookingReshedule';
import CommonReply from '../../../components/common/ui/CommonReply';

import StatsCard from '../../../components/common/ui/StatsCard';
import CustomButton from '../../../components/CustomButton';

import CustomChip from '../../../components/CustomChip';

import { FaBookmark } from 'react-icons/fa6';
import {
  BsClockFill,
  BsFillCheckCircleFill,
  BsHourglassSplit,
} from 'react-icons/bs';
import { RiTimerFlashFill } from 'react-icons/ri';
import { MdCancel, MdToday } from 'react-icons/md';

import {
  Spinner,
  Card,
  Accordion,
  AccordionItem,
  addToast,
} from '@heroui/react';

import {
  getUserBookings,
  getBookingAnalytics,
  getBookingById,
} from '../../../../service/bookingService';
import moment from 'moment';
import {
  addressFormatHelper,
  formatPaymentMethod,
} from '../../../../utils/common';
import { getTodayInBusinessTimezone } from '../../../../utils/dateUtils';
import CustomPagination from '../../../components/common/ui/CustomPagination';
import {
  useUpdateBookingStatus,
  useUpdateBookingStatusToCancelBooking,
} from '../../../../hook/useMutationData';
import CommonConfirmModal from '../../../components/common/ui/CommonConfirmModal';
import { Booking, IBookingQuery } from '../../../../utils/type';
import { useQuery } from '@tanstack/react-query';
import { formatCurrency } from '../../../../utils/currency';
import { ROLE } from '../../../../Role';
import { useFetchAllBookingsByQuery } from '../../../../hook/useQueryData';
import CustomAlert from '../../../components/CustomAlert';
import ReportButton from '../../../../components/common/ReportButton';

const BOOKING_STATUS = {
  WAITING: 'Waiting',
  IN_PROGRESS: 'InProgress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  PENDINGCAPTURE: 'PendingCapture',
} as const;

/** Returns true when the appointment start time is in the past. */
const isAppointmentExpired = (
  appointmentDate?: string,
  appointmentTimeFrom?: string
): boolean => {
  if (!appointmentDate || !appointmentTimeFrom) return false;
  const dateStr = moment(appointmentDate).format('YYYY-MM-DD');
  // Try 12-hour format first (e.g. "09:00 AM"), fall back to 24-hour
  const full = moment(`${dateStr} ${appointmentTimeFrom}`, [
    'YYYY-MM-DD hh:mm A',
    'YYYY-MM-DD HH:mm',
  ]);
  return !full.isValid() ? false : moment().isAfter(full);
};

// Utility function to format time from 24-hour to 12-hour format with am/pm
// const formatTimeTo12Hour = (time24: string): string => {
//   if (!time24) return time24;

//   // Handle time ranges like "17:00-18:00"
//   if (time24.includes('-')) {
//     const [startTime, endTime] = time24.split('-').map((t) => t.trim());
//     return `${formatTimeTo12Hour(startTime)}-${formatTimeTo12Hour(endTime)}`;
//   }

//   // Parse time like "17:00" or "17:30"
//   const [hourStr, minuteStr] = time24.split(':');
//   const hour = parseInt(hourStr, 10);
//   const minute = parseInt(minuteStr || '0', 10);

//   if (isNaN(hour)) return time24; // Return original if parsing fails

//   const period = hour >= 12 ? 'pm' : 'am';
//   const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
//   const displayMinute = minute.toString().padStart(2, '0');

//   return `${displayHour}:${displayMinute}${period}`;
// };

const formatCurrencyValue = (value?: number | string | null): string => {
  if (value === null || typeof value === 'undefined') return 'N/A';
  const numericValue =
    typeof value === 'number'
      ? value
      : Number(String(value).replace(/[^0-9.-]+/g, ''));
  if (!Number.isFinite(numericValue)) {
    return typeof value === 'string' && value.trim().length > 0 ? value : 'N/A';
  }
  return `${numericValue.toFixed(2)}`;
};

// NEW USE BOOKING
export const useBookings = (
  userId: string,
  isAuthenticated: boolean,
  filter: string,
  page: number = 1,
  appointmentDate?: string
) => {
  return useQuery({
    queryKey: ['get-all-bookings', userId, filter, page, appointmentDate],
    queryFn: () =>
      getUserBookings({ userId, status: filter, page, appointmentDate }),
    enabled: !!userId && isAuthenticated,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    retry: false,
  });
};

export const useBookingsAnalytics = (
  userId: string,
  isAuthenticated: boolean
) => {
  return useQuery({
    queryKey: ['get-all-bookings-analytics', userId],
    queryFn: () => getBookingAnalytics(userId),
    enabled: !!userId && isAuthenticated,
    staleTime: 1000 * 60 * 30,
    retry: false,
  });
};

// Predefined cancellation reasons shown to customers when cancelling a
// booking. The selected label is sent as the booking's cancellation note.
const CUSTOMER_CANCEL_REASONS = [
  { value: 'change_in_plans', label: 'Change in plans' },
  { value: 'service_no_longer_required', label: 'Service no longer required' },
  {
    value: 'unable_attend_scheduled_time',
    label: 'Unable to attend at the scheduled time',
  },
  {
    value: 'booking_details_no_longer_meet_requirements',
    label: 'Booking details no longer meet requirements',
  },
  {
    value: 'selected_another_provider',
    label: 'Selected another service provider',
  },
  { value: 'other', label: 'Other reason' },
];

const BookingList = () => {
  const { userId, isAuthenticated, loading: authLoading, role } = useAuth();
  const allowRoles = [ROLE.CUSTOMER, ROLE.PROVIDER];
  const canAccess = allowRoles.includes(role);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract bookingId from query params for email deep-linking
  const bookingIdParam = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('bookingId');
  }, [location.search]);

  // Fetch the deep-linked booking directly by ID (bypasses pagination)
  const { data: deepLinkedBooking } = useQuery({
    queryKey: ['deep-link-booking', bookingIdParam],
    queryFn: () => getBookingById(bookingIdParam!),
    enabled: !!bookingIdParam,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const todayDate = useMemo(() => getTodayInBusinessTimezone(), []);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [bookingsId, setBookingsId] = useState<string>('');
  const [action, setAction] = useState<string>('');
  const [loadingBookingId, setLoadingBookingId] = useState<string | null>(null);
  const { mutateAsync } = useUpdateBookingStatus();
  const { mutateAsync: mutateAsyncCancel, isPending: isLoadingCancel } =
    useUpdateBookingStatusToCancelBooking();

  const listFilter = useMemo(() => {
    if (activeFilter === 'today') return todayDate;
    if (activeFilter === 'waiting') return BOOKING_STATUS.WAITING;
    return activeFilter;
  }, [activeFilter, todayDate]);

  const {
    data: apiBookings,
    refetch,
    isFetching: isLoadingBooking,
  } = useBookings(
    userId ? userId : '',
    isAuthenticated,
    activeFilter === 'today' ? '' : listFilter,
    currentPage
  );

  const query: IBookingQuery = {
    fullProfile: false,
    filters: {
      customerId: userId,
      bookingStatus: [
        'Confirmed',
        'Pending',
        'InProgress',
        'Waiting',
        'Rescheduled',
      ],
    },
    isDeleted: false,
    isVisible: true,
    ownerId: userId ?? '',
    bookingProjection:
      'referenceCode,providerId,customerId,serviceId,staffId,personalInfo.firstName,personalInfo.lastName,bookingStatus,appointmentDate,appointmentTimeFrom,appointmentTimeTo,total',
    serviceProjection: 'serviceTitle,serviceId,categoryId,category.categoryId',
    staffProjection: 'staffId,firstName,lastName',
    withMongoId: false,
    page: 1,
    limit: 15,
  };

  const { data: apiBookingsAnalytics, isFetching: isLoadingBookingAnalytics } =
    useBookingsAnalytics(userId ? userId : '', isAuthenticated);
  const { data: todayBookingsData, isFetching: isLoadingTodayBookings } =
    useBookings(userId ? userId : '', isAuthenticated, '', 1, todayDate);

  const { data } = useFetchAllBookingsByQuery({
    data: query,
    enable: !!userId,
  });

  console.log('API Bookings Data:', data);

  const paginatedBookings = apiBookings?.bookings || [];

  // Merge deep-linked booking into the display list (bypasses pagination)
  const AllBooking = useMemo(() => {
    if (!deepLinkedBooking) return paginatedBookings;
    const dlBooking = deepLinkedBooking;
    const exists = paginatedBookings.some(
      (b: Booking) =>
        b.displayId === bookingIdParam ||
        (b as any).referenceCode === bookingIdParam
    );
    if (exists) {
      return paginatedBookings.map((b: Booking) =>
        b.displayId === bookingIdParam ? { ...b, _isHighlighted: false } : b
      );
    }
    return [{ ...dlBooking, _isHighlighted: false }, ...paginatedBookings];
  }, [paginatedBookings, deepLinkedBooking, bookingIdParam]);

  const totalPage = useMemo(() => apiBookings?.pages || 1, [apiBookings]);

  // Deep-link: scroll to highlighted booking card when ?bookingId= is present
  useEffect(() => {
    if (!bookingIdParam || AllBooking.length === 0) return;
    const matched = AllBooking.find(
      (b: Booking) =>
        b.displayId === bookingIdParam ||
        (b as any).referenceCode === bookingIdParam
    );
    const targetId = matched?.displayId || matched?.bookingId;
    const el = targetId ? document.getElementById(targetId) : null;
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [bookingIdParam, AllBooking]);

  const handleAddReview = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowReviewForm(true);
  };

  // Navigate to related service details
  const slugify = (s?: string) =>
    (s || 'service')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');

  const goToServiceDetails = (booking: Booking) => {
    const id = booking?.serviceId || '';
    if (!id) return;
    const name = booking?.serviceName || booking?.service || 'service';
    const slug = slugify(name);
    navigate(
      `/services/service-details/${booking?.serviceDetails?.displayId || id}/${slug}`
    );
  };

  const statusColorMap: Record<
    string,
    'success' | 'danger' | 'warning' | 'secondary' | 'primary'
  > = {
    Completed: 'success',
    Pending: 'warning',
    Confirmed: 'primary',
    InProgress: 'secondary',
    Waiting: 'warning',
    Cancelled: 'danger',
  };

  const needsCustomerCompletionConfirm = (booking: Booking) =>
    !booking?.customerMarkedCompleted &&
    booking?.providerMarkedCompleted &&
    (booking?.bookingStatus === BOOKING_STATUS.WAITING ||
      booking?.bookingStatus === BOOKING_STATUS.IN_PROGRESS);

  const handleConfirmBookingStatus = async (id: string, status: string) => {
    if (!id || !status) {
      addToast({
        title: 'Missing data',
        description: 'Missing booking data.',
        radius: 'md',
        color: 'danger',
      });
      return;
    }

    setLoadingBookingId(id); // Set loading state for this specific booking
    try {
      await mutateAsync({ id, newStatus: status });
      refetch();
      setActiveFilter('completed');
    } finally {
      setLoadingBookingId(null); // Clear loading state
    }
  };

  const handleStatusUpdate = async ({
    bookingId,
    status,
    appointmentDate,
    appointmentStartTime,
    reason,
    action,
  }: any) => {
    setBookingsId('');
    setAction('');
    if (!bookingId) {
      addToast({ description: 'Booking ID missing.', color: 'danger' });
      return;
    }

    if (!appointmentDate || !appointmentStartTime) {
      addToast({
        description: 'Appointment time information is missing.',
        color: 'danger',
      });
      return;
    }

    const now = moment();
    const appointmentFullString = `${moment(appointmentDate).format('YYYY-MM-DD')} ${appointmentStartTime}`;
    const appointmentMoment = moment(
      appointmentFullString,
      'YYYY-MM-DD hh:mm A'
    );

    const hoursDiff = appointmentMoment.diff(now, 'hours', true);

    // Only block if it is in the FUTURE (hoursDiff > 0) and less than 24h away
    const isLocked = hoursDiff > 0 && hoursDiff < 24;

    if (isLocked) {
      addToast({
        title: 'Action Restricted',
        description:
          'Cancellations/Changes are not allowed within 24 hours of the appointment.',
        color: 'danger',
      });
      return;
    }

    if (!status) {
      addToast({ description: 'Status missing.', color: 'danger' });
      return;
    }

    if (status === 'Cancelled' && !reason?.trim()) {
      addToast({
        description: 'A valid cancellation reason is required.',
        color: 'danger',
      });
      return;
    }

    try {
      setBookingsId(bookingId);
      setAction(action);

      await mutateAsyncCancel({
        id: bookingId,
        newStatus: status,
        note: reason,
      });

      if (refetch) refetch();
      setActiveFilter('cancelled');
    } catch (error: any) {
      console.error('Mutation failed', error);
      const serverErrors = error?.response?.data?.errors;
      const apiErrorMessage = Array.isArray(serverErrors)
        ? serverErrors[0]?.message || serverErrors[0]?.msg
        : serverErrors?.message ||
          error?.response?.data?.message ||
          error?.message;
      addToast({
        title: 'Unable to update booking status',
        description:
          apiErrorMessage || 'Something went wrong. Please try again later.',
        radius: 'md',
        color: 'danger',
      });
    }
  };

  const refreshApiData = () => {
    refetch();
    setActiveFilter('all');
  };

  const handleFilterSelect = (filterId: string) => {
    setActiveFilter(filterId);
    setCurrentPage(1);
  };

  return (
    <div>
      <PageHeader
        title="Bookings"
        description="Manage your service bookings and appointments."
      />

      <div className="grid grid-cols-2 tablet:grid-cols-3 laptop:grid-cols-8 flex flex-wrap gap-1 md:gap-1 mb-3 -mt-5 sm:-mt-2">
        <StatsCard
          size="md"
          filterId="today"
          isHoverEffect={true}
          isActive={activeFilter === 'today'}
          title="Today"
          value={todayBookingsData?.total || 0}
          isLoading={isLoadingTodayBookings}
          icon={<MdToday className=" text-secondary w-full h-full" />}
          iconBgClassName="bg-secondary/10"
          onClick={() => handleFilterSelect('today')}
        />
        <StatsCard
          size="md"
          filterId="all"
          isHoverEffect={true}
          isActive={activeFilter === 'all'}
          title="Total "
          value={apiBookingsAnalytics?.totalBookings || 0}
          isLoading={isLoadingBookingAnalytics}
          icon={<FaBookmark className=" text-secondary w-full h-full" />}
          iconBgClassName="bg-secondary/10"
          onClick={() => handleFilterSelect('all')}
        />
        <StatsCard
          size="md"
          filterId="pending"
          title="Pending"
          isHoverEffect={true}
          isActive={activeFilter === 'pending'}
          value={apiBookingsAnalytics?.pendingCount || 0}
          isLoading={isLoadingBookingAnalytics}
          icon={<BsClockFill className=" text-warning w-full h-full" />}
          iconBgClassName="bg-warning/10"
          onClick={() => handleFilterSelect('pending')}
        />

        <StatsCard
          size="md"
          filterId="confirmed"
          title="Confirmed"
          isHoverEffect={true}
          isActive={activeFilter === 'confirmed'}
          value={apiBookingsAnalytics?.confirmedCount || 0}
          isLoading={isLoadingBookingAnalytics}
          icon={
            <BsFillCheckCircleFill className=" text-primary w-full h-full" />
          }
          iconBgClassName="bg-primary/10"
          onClick={() => handleFilterSelect('confirmed')}
        />

        <StatsCard
          size="md"
          filterId="inprogress"
          title="In-Progress"
          isHoverEffect={true}
          isActive={activeFilter === 'inprogress'}
          value={apiBookingsAnalytics?.inprogressCount || 0}
          isLoading={isLoadingBookingAnalytics}
          icon={<RiTimerFlashFill className=" text-primary w-full h-full" />}
          iconBgClassName="bg-primary/10"
          onClick={() => handleFilterSelect('inprogress')}
        />

        <StatsCard
          size="md"
          filterId="waiting"
          title="Awaiting"
          isHoverEffect={true}
          isActive={activeFilter === 'waiting'}
          value={apiBookingsAnalytics?.waitingCount || 0}
          isLoading={isLoadingBookingAnalytics}
          icon={<BsHourglassSplit className=" text-warning w-full h-full" />}
          iconBgClassName="bg-warning/10"
          onClick={() => handleFilterSelect('waiting')}
        />

        <StatsCard
          size="md"
          filterId="completed"
          title="Completed"
          isHoverEffect={true}
          isActive={activeFilter === 'completed'}
          value={apiBookingsAnalytics?.completedCount || 0}
          isLoading={isLoadingBookingAnalytics}
          icon={
            <BsFillCheckCircleFill className=" text-success w-full h-full" />
          }
          iconBgClassName="bg-success/10"
          onClick={() => handleFilterSelect('completed')}
        />
        <StatsCard
          size="md"
          filterId="cancelled"
          title="Cancelled"
          isHoverEffect={true}
          isActive={activeFilter === 'cancelled'}
          value={apiBookingsAnalytics?.cancelledCount || 0}
          isLoading={isLoadingBookingAnalytics}
          icon={<MdCancel className=" text-danger w-full h-full" />}
          iconBgClassName="bg-danger/10"
          onClick={() => handleFilterSelect('cancelled')}
        />
      </div>

      <div className="mt-5">
        {isLoadingBooking && (
          <Card className="p-6 text-center">
            <div className="flex flex-col items-center gap-3">
              <Spinner size="lg" color="primary" label="Loading Bookings" />
            </div>
          </Card>
        )}

        {!authLoading && !isAuthenticated && !isLoadingBooking && (
          <Card className="p-6 text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="text-warning text-4xl">⚠️</div>
              <h3 className="text-title">Authentication Required</h3>
              <p className="text-body">Please log in to view your bookings.</p>
              <CustomButton
                label="Log In"
                color="primary"
                variant="solid"
                radius="sm"
                size="md"
                onPress={() => navigate('/login')}
              />
            </div>
          </Card>
        )}

        {/* Display bookings list */}
        {isAuthenticated && !isLoadingBooking && (
          <>
            {AllBooking?.length === 0 && (
              <Card className="p-6 text-center mt-5">
                <div className="flex flex-col items-center gap-3">
                  <div className="text-secondary text-4xl">📋</div>
                  <h3 className="text-title">No Bookings Found</h3>
                  <p className="text-body">
                    {activeFilter === 'all'
                      ? "You don't have any bookings yet. Start by booking a service!"
                      : `You don't have any ${activeFilter} bookings.`}
                  </p>
                  <CustomButton
                    label="Browse Services"
                    color="primary"
                    variant="solid"
                    radius="sm"
                    size="md"
                    onPress={() => navigate('/services')}
                  />
                </div>
              </Card>
            )}

            {AllBooking?.map((b: any, index: number) => (
              <FullCustomCardWithHeader
                key={index}
                id={b?.displayId || b?.bookingId}
                title={b?.serviceName || b?.service || 'Service'}
                subtitle={b?.businessName || '-'}
                isHighlighted={false}
                isChip={true}
                chipText={
                  b?.bookingStatus === 'InProgress'
                    ? 'In-Progress'
                    : b?.bookingStatus || b?.status
                }
                chipVariant="dot"
                chipColor={
                  statusColorMap[b?.bookingStatus || b?.status] || 'secondary'
                }
                className=" hover:shadow-md mt-5"
                mainContent={
                  <div className="-mt-3">
                    <div className="grid grid-cols-1 tablet:grid-cols-4 laptop:grid-cols-6 text-body gap-4">
                      <div className="flex flex-col ">
                        <p className="text-body">Appointment ID</p>
                        <p className="text-body-bold font-primary">
                          {b?.referenceCode || b?.bookingId || b?.id || '-'}
                        </p>
                      </div>

                      <div className="flex flex-col ">
                        <p className="text-body">Appointment Date</p>
                        <p className="text-body-bold font-primary">
                          {b?.appointmentDate
                            ? moment(b.appointmentDate).format('DD MMM YYYY')
                            : b?.date
                              ? b.date.includes(',')
                                ? b.date.split(',')[0]
                                : b.date
                              : '-'}
                        </p>
                      </div>

                      <div className="flex flex-col">
                        <p className="text-body">Appointment Time</p>
                        <p className="text-body-bold font-primary">
                          {b?.appointmentTimeFrom || b?.appointmentTimeTo
                            ? `${b?.appointmentTimeFrom || '-'} - ${b?.appointmentTimeTo || '-'}`
                            : b?.date && b.date.includes(',')
                              ? b.date.split(',')[1]?.trim() || '-'
                              : '-'}
                        </p>
                      </div>

                      {'isPaid' in (b || {}) && (
                        <div className="flex flex-col ">
                          <p className="text-body">Paid Status</p>
                          {/* <CustomChip
                            label={b.isPaid ? 'Paid' : 'Unpaid'}
                            color={b.isPaid ? 'success' : 'danger'}
                          /> */}
                          <p className="text-body-bold">Not Applicable</p>
                        </div>
                      )}

                      <div className="flex flex-col">
                        <p className="text-body">Payment Method</p>
                        <p className="text-body-bold">
                          {formatPaymentMethod(
                            b?.paymentMethod ?? b?.payment
                          ) || '-'}
                        </p>
                      </div>

                      <div className="flex flex-col">
                        <p className="text-body">
                          {b?.bookingType === 'PackageSession'
                            ? 'Sessions'
                            : 'Total'}
                        </p>
                        <p className="text-body-bold">
                          {b?.bookingType === 'PackageSession' &&
                          typeof b?.sessionsUsed === 'number' &&
                          typeof b?.sessionsTotal === 'number' ? (
                            <>
                              Session {b.sessionsUsed} of {b.sessionsTotal}
                            </>
                          ) : (
                            formatCurrency(Number(b?.total)) || 'N/A'
                          )}
                        </p>
                      </div>

                      <div className="flex flex-col">
                        <p className="text-body">Appointment Created On</p>
                        <p className="text-body-bold">
                          {b?.createdAt
                            ? moment(b.createdAt).format('DD MMM YYYY, hh:mm A')
                            : '-'}
                        </p>
                      </div>

                      {b?.personalInfo?.address && (
                        <div className="flex flex-col">
                          <p className="text-body">Address</p>
                          <p className="text-body-bold">
                            {addressFormatHelper(b.personalInfo?.address)}
                          </p>
                        </div>
                      )}

                      <div className="flex flex-col">
                        <p className="text-body">Provider Email</p>
                        <p className="text-body-bold">
                          {b?.businessEmail || '-'}
                        </p>
                      </div>

                      <div className="flex flex-col">
                        <p className="text-body">Provider Contact No</p>
                        <p className="text-body-bold">
                          {b?.businessPhone || '-'}
                        </p>
                      </div>

                      <div className="flex flex-col">
                        <p className="text-body">Changed Booking</p>
                        {(() => {
                          const rescheduleLogs = b?.auditLogs?.filter(
                            (log: any) => log?.action === 'Rescheduled Booking'
                          );
                          return (
                            <CustomChip
                              variant="dot"
                              label={
                                rescheduleLogs?.length > 0
                                  ? 'Rescheduled'
                                  : 'No'
                              }
                              color={
                                rescheduleLogs?.length > 0
                                  ? 'warning'
                                  : 'success'
                              }
                            />
                          );
                        })()}
                      </div>

                      {/* <div className="flex flex-col">
                        <p className="text-body">Last Reschedule</p>
                        {(() => {
                          const rescheduleLogs = b?.auditLogs?.filter(
                            (log: any) => log?.action === 'Rescheduled Booking'
                          );
                          if (!rescheduleLogs || rescheduleLogs.length === 0) {
                            return <p className="text-body-bold">-</p>;
                          }
                          const lastReschedule =
                            rescheduleLogs[rescheduleLogs.length - 1];
                          return (
                            <p className="text-body-bold">
                              {moment(lastReschedule.timestamp).format(
                                'DD MMM YYYY, hh:mm A'
                              )}
                            </p>
                          );
                        })()}
                      </div> */}

                      <div className="flex flex-col">
                        <p className="text-body">Staff Name</p>
                        <p className="text-body-bold">
                          {b?.staffName ||
                            b?.staff?.name ||
                            b?.staff?.fullName ||
                            '-'}
                        </p>
                      </div>
                    </div>

                    {/* BOOKING NOTE */}
                    {b?.personalInfo?.bookingNotes && (
                      <CommonReply
                        title="Special Instructions"
                        description={b?.personalInfo?.bookingNotes}
                      />
                    )}

                    {/* ADDTIONAL DETAILS */}
                    {b?.additionalServices?.length > 0 && (
                      <div className="mt-3  bg-white rounded-xl">
                        <Accordion
                          isCompact
                          variant="bordered"
                          fullWidth
                          className="mt-2"
                        >
                          <AccordionItem
                            classNames={{
                              title: 'text-body-bold',
                            }}
                            key="1"
                            aria-label={b?.bookingId}
                            title={`${'Additional Service'} (${
                              b?.additionalServices?.length
                            })`}
                          >
                            <div className="grid grid-cols-2 tablet:grid-cols-4 laptop:grid-cols-7 text-body gap-4">
                              <div className="flex flex-col ">
                                <p className="text-body mb-1">Service Name</p>
                                {b?.additionalServices?.map(
                                  (a: any, index: number) => (
                                    <p
                                      key={index}
                                      className="text-body-bold font-primary mb-1"
                                    >
                                      {a?.serviceItem || '-'}
                                    </p>
                                  )
                                )}
                              </div>

                              <div className="flex flex-col ">
                                <p className="text-body mb-1">Price</p>
                                {b?.additionalServices?.map(
                                  (a: any, index: number) => (
                                    <p
                                      key={index}
                                      className="text-body-bold font-primary mb-1"
                                    >
                                      {formatCurrency(Number(a?.price)) || '-'}
                                    </p>
                                  )
                                )}
                              </div>
                            </div>
                          </AccordionItem>
                        </Accordion>
                      </div>
                    )}

                    {/* SESSION DETAILS (for PackageSession bookings) */}
                    {b?.bookingType === 'PackageSession' &&
                      typeof b?.sessionsRemaining === 'number' &&
                      typeof b?.sessionsTotal === 'number' && (
                        <div className="mt-2  bg-white rounded-xl">
                          <Accordion
                            isCompact
                            variant="bordered"
                            fullWidth
                            className="mt-2"
                          >
                            <AccordionItem
                              classNames={{
                                title: 'text-body-bold',
                              }}
                              key="1"
                              aria-label={b?.referenceCode}
                              title="Session Details"
                            >
                              <div className="grid grid-cols-2 tablet:grid-cols-4 laptop:grid-cols-7 text-body gap-4">
                                <div className="col-span-3">
                                  <div className="flex justify-between items-center border-b mb-2">
                                    <p className="text-body-bold mb-1">
                                      Sessions Used
                                    </p>
                                    <p className="text-body-bold ">
                                      {b.sessionsUsed || 0}
                                    </p>
                                  </div>
                                  <div className="flex justify-between items-center border-b mb-2">
                                    <p className="text-body-bold mb-1">
                                      Total Sessions
                                    </p>
                                    <p className="text-body-bold ">
                                      {b.sessionsTotal}
                                    </p>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <p className="text-body-bold mb-1 mt-1">
                                      Sessions Remaining
                                    </p>
                                    <p className="text-title ">
                                      {b.sessionsRemaining}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </AccordionItem>
                          </Accordion>
                        </div>
                      )}

                    {/* PAYMENT DETAILS (for Slot bookings) */}
                    {b?.bookingType !== 'PackageSession' && (
                      <div className="mt-2  bg-white rounded-xl">
                        <Accordion
                          isCompact
                          variant="bordered"
                          fullWidth
                          className="mt-2"
                        >
                          <AccordionItem
                            classNames={{
                              title: 'text-body-bold',
                            }}
                            key="1"
                            aria-label={b?.referenceCode}
                            title="Payment Details"
                          >
                            <div className="grid grid-cols-2 tablet:grid-cols-4 laptop:grid-cols-7 text-body gap-4">
                              <div className="col-span-3">
                                <div className="flex justify-between items-center border-b mb-2">
                                  <p className="text-body-bold mb-1">
                                    Service Price
                                  </p>
                                  <p className="text-body-bold ">
                                    {formatCurrency(
                                      Number(
                                        formatCurrencyValue(
                                          typeof b?.serviceDetails?.price !==
                                            'undefined' &&
                                            b?.serviceDetails?.price !== null
                                            ? b.serviceDetails.price
                                            : (b?.price ?? null)
                                        )
                                      )
                                    )}
                                  </p>
                                </div>

                                {b?.additionalServices?.length > 0 && (
                                  <div className="mb-2">
                                    <p className="text-caption mb-2 ">
                                      Additional Services
                                    </p>
                                    <ul className="list-none space-y-1 mt-1">
                                      {b?.additionalServices?.map(
                                        (s: any, index: number) => (
                                          <li
                                            key={index}
                                            className="flex justify-between text-body-bold"
                                          >
                                            <p>
                                              {index + 1}. {s?.serviceItem}
                                            </p>
                                            <p className=" ml-2 text-body1">
                                              {formatCurrency(
                                                s?.price?.toFixed(2)
                                              ) || '0.00'}
                                            </p>
                                          </li>
                                        )
                                      )}
                                    </ul>
                                  </div>
                                )}
                                <div className="flex justify-between items-center border-t mt-2">
                                  <p className="text-body-bold mb-1 mt-1">
                                    Subtotal
                                  </p>
                                  <p className="text-body-bold ">
                                    {formatCurrency(b?.subtotal) || '0.00'}
                                  </p>
                                </div>
                                {/* <div className="flex justify-between items-center mt-1">
                                  <p className="text-success mb-1">Discount</p>
                                  <p className="text-success ">
                                    -{' '}
                                    {formatCurrency(-Number(b?.discount || '0.00'))}
                                  </p>
                                </div>
                                <div className="flex justify-between items-center border-b mt-1">
                                  <p className="text-danger mb-1">Tax</p>
                                  <p className="text-danger ">
                                    + {formatCurrency(Number(b?.tax || '0.00'))}
                                  </p>
                                </div> */}
                                <div className="flex justify-between items-center mt-1 border-b last:border-b-0">
                                  <p className="text-body-bold mb-1">Price</p>
                                  <p className="text-title ">
                                    {/* ${b?.total || 'N/A'} */}
                                    {formatCurrency(
                                      Number(
                                        formatCurrencyValue(
                                          typeof (b as any)?.b?.total !==
                                            'undefined' &&
                                            (b as any)?.b?.total !== null
                                            ? (b as any).b.total
                                            : ((b as any)?.total ?? null)
                                        )
                                      )
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </AccordionItem>
                        </Accordion>
                      </div>
                    )}

                    {/* Cancellation Reason */}
                    {b?.bookingStatus === BOOKING_STATUS.CANCELLED && (
                      <div className="mt-2  bg-white rounded-xl">
                        <Accordion
                          isCompact
                          variant="bordered"
                          fullWidth
                          className="mt-2"
                        >
                          <AccordionItem
                            classNames={{
                              title: 'text-body-bold',
                              content: 'flex justify-start items-center',
                            }}
                            key="1"
                            aria-label={b?.referenceCode}
                            title="Cancellation Reason"
                          >
                            <p className="text-body">
                              {b?.cancelledNote || 'N/A'}
                            </p>
                          </AccordionItem>
                        </Accordion>
                      </div>
                    )}

                    {/* RESHEDULE */}
                    {canAccess && (
                      <>
                        {b?.bookingStatus !== BOOKING_STATUS.CANCELLED &&
                          b?.bookingStatus !== BOOKING_STATUS.COMPLETED &&
                          b?.bookingStatus !== BOOKING_STATUS.IN_PROGRESS &&
                          b?.bookingStatus !== BOOKING_STATUS.WAITING &&
                          !(
                            b?.bookingStatus ===
                              BOOKING_STATUS.PENDINGCAPTURE && !b?.isPaid
                          ) &&
                          !b?.customerMarkedCompleted &&
                          !b?.providerMarkedCompleted && (
                            <div className="mt-2 bg-white rounded-xl">
                              <Accordion
                                isCompact
                                variant="bordered"
                                fullWidth
                                className="mt-2"
                              >
                                <AccordionItem
                                  classNames={{
                                    title: 'text-body-bold',
                                    content: 'flex justify-center items-center',
                                  }}
                                  key="1"
                                  aria-label={b?.referenceCode}
                                  title="Change Appointment"
                                >
                                  <div className="w-full">
                                    <BookingReshedule
                                      data={b}
                                      onSuccessCallback={refreshApiData}
                                    />
                                  </div>
                                </AccordionItem>
                              </Accordion>
                            </div>
                          )}
                      </>
                    )}

                    {canAccess && (
                      <div className="mt-2">
                        {needsCustomerCompletionConfirm(b) && (
                          <CustomAlert
                            title="Your service provider has marked this service as complete"
                            description="Please inspect the work and confirm completion. If no action is taken, this booking will automatically be marked as completed after 48 hours."
                            color="success"
                            variant="flat"
                            endContent={
                              <CustomButton
                                label="Confirm"
                                color="success"
                                size="sm"
                                variant="solid"
                                onPress={() =>
                                  handleConfirmBookingStatus(
                                    b?.bookingId,
                                    'Completed'
                                  )
                                }
                                isLoading={loadingBookingId === b?.bookingId}
                              />
                            }
                          />
                        )}
                      </div>
                    )}

                    {b?.bookingStatus !== BOOKING_STATUS.COMPLETED &&
                      b?.bookingStatus !== BOOKING_STATUS.CANCELLED &&
                      isAppointmentExpired(
                        b?.appointmentDate,
                        b?.appointmentTimeFrom
                      ) && (
                        <div className="mt-2">
                          <CustomAlert
                            title="Appointment Expired"
                            description="The appointment time has expired and the booking can no longer be cancelled. If you wish to continue, please select the next available appointment date."
                            color="warning"
                            variant="faded"
                            hideIcon
                          />
                        </div>
                      )}
                  </div>
                }
                footerContent={
                  <div className="flex flex-row gap-3 flex-wrap">
                    {canAccess && (
                      <>
                        {b?.bookingStatus === BOOKING_STATUS.COMPLETED &&
                          !b?.hasCusReviewed && (
                            <CustomButton
                              label="Add Review"
                              color="secondary"
                              variant="solid"
                              radius="sm"
                              size="sm"
                              onPress={() => handleAddReview(b)}
                            />
                          )}
                      </>
                    )}

                    {canAccess && (
                      <>
                        {b?.paymentMethod === 'Credit/Debit Card' &&
                          b?.bookingStatus === BOOKING_STATUS.PENDINGCAPTURE &&
                          !b?.isPaid &&
                          b?.referenceCode && (
                            <CustomButton
                              label="Make Payment"
                              color="success"
                              variant="solid"
                              radius="sm"
                              size="sm"
                              onPress={() =>
                                navigate(`/booking/pay/${b.referenceCode}`)
                              }
                            />
                          )}
                      </>
                    )}

                    {canAccess && (
                      <>
                        {b?.serviceId && (
                          <CustomButton
                            label="View Service"
                            color="primary"
                            variant="solid"
                            radius="sm"
                            size="sm"
                            onPress={() => goToServiceDetails(b)}
                          />
                        )}
                      </>
                    )}

                    <ReportButton
                      targetType="booking"
                      targetId={b?.bookingId || ''}
                      variant="link"
                    />

                    {canAccess && (
                      <>
                        {b?.bookingStatus !== BOOKING_STATUS.COMPLETED &&
                          b?.bookingStatus !== BOOKING_STATUS.CANCELLED &&
                          b?.bookingStatus !== BOOKING_STATUS.IN_PROGRESS &&
                          b?.bookingStatus !== BOOKING_STATUS.WAITING &&
                          !b?.providerMarkedCompleted &&
                          (!b?.customerMarkedCompleted ||
                            !b?.providerMarkedCompleted) &&
                          !isAppointmentExpired(
                            b?.appointmentDate,
                            b?.appointmentTimeFrom
                          ) && (
                            <CommonConfirmModal
                              buttonLabel="Cancel Booking"
                              buttonColor="danger"
                              title="Cancel Booking?"
                              confirmBtnText="Confirm"
                              confirmBtnColor="danger"
                              iconType="danger"
                              isLoading={
                                b?.bookingId === bookingsId &&
                                action === 'Cancel' &&
                                isLoadingCancel
                              }
                              requireReason={true}
                              reasonRequired={true}
                              reasonMinLength={10}
                              reasonOptions={CUSTOMER_CANCEL_REASONS}
                              otherOptionValue="other"
                              reasonLabel="Cancellation Reason"
                              reasonDescription="Please provide a reason for cancelling this booking."
                              description={`Are you sure you want to cancel the booking?. This action cannot be undone.`}
                              data={{ bookingId: b?.bookingId }}
                              onConfirm={(reason, data) => {
                                handleStatusUpdate({
                                  bookingId: data?.bookingId,
                                  status: 'Cancelled',
                                  appointmentDate: b?.appointmentDate,
                                  appointmentStartTime: b?.appointmentTimeFrom,
                                  reason,
                                  action: 'Cancel',
                                });
                              }}
                            />
                          )}
                      </>
                    )}
                  </div>
                }
              />
            ))}
          </>
        )}

        {/* Pagination */}
        {isAuthenticated && AllBooking?.length > 0 && (
          <div className="flex justify-end items-end py-5 mt-7">
            <CustomPagination
              page={currentPage}
              initialPage={1}
              total={totalPage}
              size="md"
              onChange={setCurrentPage}
            />
          </div>
        )}
      </div>
      {/* Review Modal */}
      <ReviewForm
        isOpen={showReviewForm}
        onClose={() => setShowReviewForm(false)}
        onSuccess={() => {
          // Refetch bookings after review is successfully submitted
          // fetchBookings();
          refreshApiData();
        }}
        providerId={selectedBooking?.providerId}
        serviceId={
          selectedBooking?.serviceId || String(selectedBooking?.id || '')
        }
        bookingId={
          selectedBooking?.bookingId || String(selectedBooking?.id || '')
        }
        serviceName={selectedBooking?.serviceName || selectedBooking?.service}
      />
    </div>
  );
};

export default BookingList;
